import cookies from 'js-cookie';

import { AnalyticsEvent, ExceptionDimensions, PurchaseDimensions, SalesEventItem, logAnalyticsEvent } from '@vestico/utils';

import { getUserFromStorage, writeUserToStorage } from '@vestico/model';

import { AuthInfo, ConversionConfigurations } from '../model/api';
import { AuthApiClient } from './apiClientAuth';
import { ConversionApiClient } from './apiClientConversion';
import { vesticoAuthCookieKey } from './helpers';

const recordConversionInFirestore = async (config: ConversionConfigurations) => {
  // Record conversion in firestore
  const { clientId, sdkAPIKey, iOSBundleIdentifier, experiments } = config;

  const conversionAPI = new ConversionApiClient(clientId, false, sdkAPIKey, iOSBundleIdentifier, experiments);

  const existingAuthInfo = cookies.get(vesticoAuthCookieKey);
  let authInfo: AuthInfo | null = null;
  let isRefetch = false;

  if (existingAuthInfo && existingAuthInfo !== undefined) {
    console.debug('using auth info from cookies');
    authInfo = JSON.parse(existingAuthInfo);

    // reset auth info if expired
    if (authInfo && (!authInfo.expiryDate || authInfo.expiryDate < Date.now())) {
      authInfo = null;
      isRefetch = true;

      console.warn('Reset auth info during conversion. reason: expired');
      logAnalyticsEvent({
        category: 'exception',
        event: 'record-conversion-reset-auth',
        name: 'Reason: expired',
        dimensions: { provider: clientId } as ExceptionDimensions,
      });
    }
  }

  if (!authInfo) {
    const authAPI = new AuthApiClient(clientId, false, sdkAPIKey, iOSBundleIdentifier);
    authInfo = (await authAPI.fetchAuthInfo(isRefetch)) as AuthInfo;
  }

  if (!authInfo) {
    console.error('Could not fetch auth info');
    logAnalyticsEvent({
      category: 'exception',
      event: 'record-conversion',
      name: 'Could not fetch auth info',
      dimensions: { provider: clientId } as ExceptionDimensions,
    });
    return;
  }

  const resetAuth = () => {
    const authAPI = new AuthApiClient(clientId, false, sdkAPIKey, iOSBundleIdentifier);
    return authAPI.fetchAuthInfo(true);
  };

  const user = getUserFromStorage();
  const response = await conversionAPI.recordConversion(config.orderUID, config.items, user, { authInfo, resetAuth });

  // Store the new user uid in storage
  if (response && !user.uid) {
    user.uid = response.user;
    writeUserToStorage(user);
  }
};

export const recordConversion = async (config: ConversionConfigurations) => {
  const { clientId, items } = config;
  const { orderUID } = config;

  if (!orderUID || !clientId || items.length === 0) {
    if (!orderUID) {
      console.error('Cannot record conversion: missing order uid');
    }
    if (!clientId) {
      console.error('Cannot record conversion: missing client id');
    }

    if (!orderUID || !clientId) {
      logAnalyticsEvent({
        category: 'exception',
        event: 'record-conversion',
        name: 'Missing required values',
        dimensions: { provider: clientId, description: JSON.stringify(orderUID ?? '') } as ExceptionDimensions,
      });

      return;
    }
  }

  // Log analytics first
  const purchaseParams: PurchaseDimensions = {
    provider: clientId,
    transaction_id: orderUID,
    items: items
      ? items.map((item) => {
          const purchaseItem: SalesEventItem = {
            item_id: item.productUID,
            item_variant: item.variant ?? item.size,
          };
          return purchaseItem;
        })
      : [],
  };

  await recordConversionInFirestore(config);
  logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.purchase, dimensions: purchaseParams });
};
